import { useMemo } from 'react'

import type { ChartTypeV2 as ChartType } from 'features/charts-v2/types/chart-types'
import { useDetailData } from 'features/trend-detection/features/issue-detail/hooks/use-detail-data'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  Chart,
  IssueCardData,
  PostText,
  TrendFeedIssueItem,
} from 'features/trend-detection/types'
import type { IssueBreakdownSelectionValue } from 'features/trend-detection/utils/dimension-mappings'

export interface UseIssueDetailResult {
  issueItem: TrendFeedIssueItem | null
  issueCard: IssueCardData | null
  issueLoading: boolean
  issueError: boolean

  timeseriesChart: Chart | undefined
  projectTotalChart: Chart | undefined
  timeseriesLoading: boolean

  breakdownChart: Chart | null | undefined
  breakdownLoading: boolean
  selectedBreakdownDimension: IssueBreakdownSelectionValue
  breakdownChartType: ChartType
  onBreakdownDimensionChange: (dim: IssueBreakdownSelectionValue) => void
  onBreakdownChartTypeChange: (type: ChartType) => void
  refetchBreakdowns: (dim?: IssueBreakdownSelectionValue) => void

  postTexts: PostText[]
  verbatimsLoading: boolean
}

export function useIssueDetail(
  service: TrendDetectionService,
  projectId: string | null,
  issueUuid: string | null,
): UseIssueDetailResult {
  const config = useMemo(
    () => ({
      queryKey: 'issue-detail',
      fetchFn: (pid: string, uuid: string) =>
        service.fetchIssueDetail(pid, uuid),
      getIssueUuid: (item: TrendFeedIssueItem) => item.issue?.uuid ?? null,
      getRunUuid: (item: TrendFeedIssueItem) => item.run_uuid ?? null,
      getIssue: (item: TrendFeedIssueItem) => item.issue ?? null,
    }),
    [service],
  )

  const detail = useDetailData<TrendFeedIssueItem>(
    service,
    projectId,
    issueUuid,
    config,
  )

  return {
    issueItem: detail.item,
    issueCard: detail.cardData,
    issueLoading: detail.loading,
    issueError: detail.error,
    timeseriesChart: detail.timeseriesChart,
    projectTotalChart: detail.projectTotalChart,
    timeseriesLoading: detail.timeseriesLoading,
    breakdownChart: detail.breakdownChart,
    breakdownLoading: detail.breakdownLoading,
    selectedBreakdownDimension: detail.selectedBreakdownDimension,
    breakdownChartType: detail.breakdownChartType,
    onBreakdownDimensionChange: detail.onBreakdownDimensionChange,
    onBreakdownChartTypeChange: detail.onBreakdownChartTypeChange,
    refetchBreakdowns: detail.refetchBreakdowns,
    postTexts: detail.postTexts,
    verbatimsLoading: detail.verbatimsLoading,
  }
}
